import { useEffect, useState, useRef } from "react";
import { api, fmtDate, formatApiError, API_URL } from "@/lib/api";
import { DatePicker } from "@/components/DatePicker";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "./Quotations";
import { OutcomeBadges } from "@/components/OutcomeBadges";
import { Plus, Route, Radio, Pencil, Tag, Trash2, Paperclip, Mail, Printer, FileCheck2, Upload } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";


const emptyForm = {
  sales_order_id: "", total_boxes: 0, invoice_number: "", lr_number: "",
  transporter: "", transporter_id: "", transporter_gstin: "", eway_bill_number: "",
  from_pincode: "401202", to_pincode: "", distance_km: 0, eway_validity_days: 0,
  dispatch_date: "", delivery_status: "Pending",
};

export default function Dispatch() {
  const [list, setList] = useState([]);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dTab, setDTab] = useState("active");
  const shownDisp = list.filter((d) => (dTab === "completed" ? d.delivery_status === "Delivered" : d.delivery_status !== "Delivered"));
  const pager = usePager(shownDisp);
  const [prevLr, setPrevLr] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [finder, setFinder] = useState({ from: "401202", to: "", result: null, loading: false });
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const emailDispatch = async (d) => {
    if (!d.lr_number) { toast.error("Add the LR / Docket number first"); return; }
    if (!window.confirm(`Email invoice & dispatch details to the customer for ${d.order_number}?`)) return;
    const tid = toast.loading("Sending dispatch email…");
    try {
      const r = await api.post(`/dispatch/${d.id}/email`);
      toast.success(`Emailed to ${r.data.to}${r.data.invoice_attached ? " (invoice PDF attached)" : ""}`, { id: tid });
    } catch (e) { toast.error(formatApiError(e), { id: tid }); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete dispatch for ${d.order_number || d.invoice_number || "this order"}?`)) return;
    try { await api.delete(`/dispatch/${d.id}`); toast.success("Dispatch deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const load = () => Promise.all([
    api.get("/dispatch").then((r) => setList(r.data)),
    api.get("/sales-orders").then((r) => setOrders(r.data)),
    api.get("/invoices").then((r) => setInvoices(r.data)),
    api.get("/transporters").then((r) => setTransporters(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const pickOrder = async (oid) => {
    const inv = invoices.find((x) => x.sales_order_id === oid);
    const o = orders.find((x) => x.id === oid);
    const fromPin = inv?.from_pincode || form.from_pincode || "401202";
    const toPin = inv?.to_pincode || o?.customer_snapshot?.shipping_pincode || o?.customer_snapshot?.billing_pincode || form.to_pincode;
    setForm((f) => ({ ...f, sales_order_id: oid,
      invoice_number: inv?.number || f.invoice_number,
      transporter: inv?.transport || f.transporter,
      transporter_id: inv?.transporter_id || f.transporter_id,
      transporter_gstin: transporters.find((t) => t.id === inv?.transporter_id)?.gstin || f.transporter_gstin,
      eway_bill_number: inv?.eway_bill_number || f.eway_bill_number,
      from_pincode: fromPin,
      to_pincode: toPin,
      distance_km: inv?.distance_km || f.distance_km,
      eway_validity_days: inv?.eway_validity_days || f.eway_validity_days }));
    if (inv) toast.success(`Invoice ${inv.number} details auto-filled (transporter, E-Way, distance)`);
    if (!inv?.distance_km && /^\d{6}$/.test(fromPin) && /^\d{6}$/.test(toPin)) {
      try {
        const r = await api.get("/utils/pin-distance", { params: { from_pin: fromPin, to_pin: toPin } });
        setForm((f) => ({ ...f, distance_km: r.data.road_km_estimate, eway_validity_days: r.data.eway_validity_days }));
        toast.success(`Distance auto-calculated: ≈ ${r.data.road_km_estimate} km (${fromPin} → ${toPin})`);
      } catch { /* manual calc still available */ }
    }
  };

  const calcFormDistance = async () => {
    if (!/^\d{6}$/.test(form.from_pincode) || !/^\d{6}$/.test(form.to_pincode)) {
      toast.error("Enter valid 6-digit pincodes"); return;
    }
    try {
      const r = await api.get("/utils/pin-distance", { params: { from_pin: form.from_pincode, to_pin: form.to_pincode } });
      setForm((f) => ({ ...f, distance_km: r.data.road_km_estimate, eway_validity_days: r.data.eway_validity_days }));
      toast.success(`≈ ${r.data.road_km_estimate} km — E-Way validity ${r.data.eway_validity_days} day(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const findDistance = async () => {
    if (!/^\d{6}$/.test(finder.from) || !/^\d{6}$/.test(finder.to)) {
      toast.error("Enter valid 6-digit pincodes"); return;
    }
    setFinder((f) => ({ ...f, loading: true }));
    try {
      const r = await api.get("/utils/pin-distance", { params: { from_pin: finder.from, to_pin: finder.to } });
      setFinder((f) => ({ ...f, result: r.data, loading: false }));
    } catch (e) { toast.error(formatApiError(e)); setFinder((f) => ({ ...f, loading: false })); }
  };

  const custPhone = (soId) => {
    const o = orders.find((x) => x.id === soId);
    const digits = (o?.customer_snapshot?.mobile || "").replace(/\D/g, "");
    return digits.length === 10 ? `91${digits}` : digits;
  };

  const buildTrackUrl = (base, lr) => {
    if (!base || !lr) return "";
    if (base.includes("{lr}")) return base.replace("{lr}", lr);
    if (base.endsWith("=") || base.endsWith("/")) return base + lr;
    return base;
  };

  const openWhatsApp = (d) => {
    const t = transporters.find((x) => x.id === d.transporter_id) ||
              transporters.find((x) => x.name === d.transporter);
    const injected = buildTrackUrl(t?.tracking_url || "", d.lr_number);
    const canInject = injected && injected !== (t?.tracking_url || "");
    const link = injected || `${window.location.origin}/track/${d.tracking_token}`;
    const trackLine = canInject || !injected
      ? `Track it live here: ${link}`
      : `Track it here: ${link} (enter LR/Docket no ${d.lr_number})`;
    const o = orders.find((x) => x.id === d.sales_order_id);
    const name = o?.customer_snapshot?.company_name || "Customer";
    const msg = `Dear ${name},\n\nYour Hi-Tech Silico order ${d.order_number} has been dispatched!\nLR/Docket: ${d.lr_number}${d.transporter ? `\nTransporter: ${d.transporter}` : ""}${d.eway_bill_number ? `\nE-Way Bill: ${d.eway_bill_number}` : ""}\n${trackLine}\n\nRegards,\nHi-Tech Silico\n+91 8888837157`;
    window.open(`https://wa.me/${custPhone(d.sales_order_id)}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const [ewayFile, setEwayFile] = useState(null);

  const viewFile = async (fid) => {
    try {
      const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open file"); }
  };

  const save = async () => {
    if (!form.sales_order_id) { toast.error("Select a sales order"); return; }
    try {
      const b = { ...form, distance_km: +form.distance_km || 0, eway_validity_days: +form.eway_validity_days || 0, total_boxes: +form.total_boxes || 0 };
      delete b.eway_file_id; delete b.eway_file_name;
      if (!b.dispatch_date) delete b.dispatch_date;
      let saved;
      if (editing) {
        const r = await api.put(`/dispatch/${editing}`, b);
        saved = r.data;
        toast.success("Dispatch updated");
      } else {
        const r = await api.post("/dispatch", b);
        saved = r.data;
        toast.success("Dispatch created");
      }
      if (ewayFile && saved?.id) {
        try {
          const fd = new FormData();
          fd.append("file", ewayFile);
          const up = await api.post(`/dispatch/${saved.id}/eway-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success(up.data.invoice_number
            ? `E-Way bill attached to invoice ${up.data.invoice_number}`
            : "E-Way bill file saved");
        } catch (e) { toast.error(formatApiError(e)); }
      }
      const lrJustAdded = (form.lr_number || "").trim() && (!editing || !prevLr.trim());
      setOpen(false); setEditing(null); setForm(emptyForm); setPrevLr(""); setEwayFile(null); load();
      if (lrJustAdded) {
        toast.success("LR saved — sharing tracking link on WhatsApp");
        openWhatsApp(saved);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (d) => {
    setEditing(d.id);
    setPrevLr(d.lr_number || "");
    setForm({ sales_order_id: d.sales_order_id, total_boxes: d.total_boxes || 0,
              invoice_number: d.invoice_number || "", lr_number: d.lr_number || "",
              transporter: d.transporter || "", transporter_id: d.transporter_id || "",
              transporter_gstin: d.transporter_gstin || "", eway_bill_number: d.eway_bill_number || "",
              from_pincode: d.from_pincode || "401202", to_pincode: d.to_pincode || "",
              distance_km: d.distance_km || 0, eway_validity_days: d.eway_validity_days || 0,
              dispatch_date: d.dispatch_date ? String(d.dispatch_date).slice(0, 10) : "",
              delivery_status: d.delivery_status || "Pending",
              eway_file_id: d.eway_file_id || "", eway_file_name: d.eway_file_name || "" });
    setEwayFile(null);
    setOpen(true);
  };
  const podInputRef = useRef(null);
  const [podFor, setPodFor] = useState(null);
  const pickPod = (d) => { setPodFor(d); setTimeout(() => podInputRef.current?.click(), 0); };
  const uploadPod = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !podFor) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/dispatch/${podFor.id}/pod-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`POD uploaded — ${podFor.order_number} marked Delivered`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setPodFor(null);
  };
  const viewPod = async (fid) => {
    try {
      const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open POD file"); }
  };

  const trackLiveUrl = (d) => {
    const t = transporters.find((x) => x.id === d.transporter_id);
    if (!t?.tracking_url) return null;
    return t.tracking_url.includes("{lr}") ? t.tracking_url.replace("{lr}", encodeURIComponent(d.lr_number || "")) : t.tracking_url;
  };

  return (
    <div className="space-y-4" data-testid="dispatch-page">
      <PageHeader title="Dispatch & E-Way" subtitle="LR/Docket tracking, E-Way bills & live delivery status"
        right={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); setForm(emptyForm); }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="dp-new">
                <Plus size={14} className="mr-1" /> New Dispatch
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Dispatch</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Sales Order *</Label>
                  <Select value={form.sales_order_id} onValueChange={pickOrder} disabled={!!editing}>
                    <SelectTrigger className="mt-1.5" data-testid="dp-order"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {orders.filter((o) => (editing && form.sales_order_id === o.id) ||
                        ((!o.po_status || o.po_status === "Received") &&
                          (o.invoicing_type === "consignment" ? !o.invoiced
                            : !["Dispatched", "Delivered", "Cancelled", "Closed"].includes(o.status))))
                        .map((o) => <SelectItem key={o.id} value={o.id}>{o.number} · {o.customer_snapshot?.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Transporter</Label>
                  <Select value={form.transporter_id} onValueChange={(v) => {
                    const t = transporters.find((x) => x.id === v);
                    setForm({ ...form, transporter_id: v, transporter: t?.name || "", transporter_gstin: t?.gstin || "" });
                  }}>
                    <SelectTrigger className="mt-1.5" data-testid="dp-transporter"><SelectValue placeholder="Select transporter" /></SelectTrigger>
                    <SelectContent>
                      {transporters.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      {transporters.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No transporters — add under Transporters</div>}
                    </SelectContent>
                  </Select>
                  {form.transporter_gstin && <p className="text-[11px] text-slate-500 mt-1 font-mono">GST: {form.transporter_gstin}</p>}
                </div>
                <F l="LR / Docket Number" v={form.lr_number} on={(v) => setForm({ ...form, lr_number: v })} tid="dp-lr" />
                <div className="grid grid-cols-2 gap-2 items-end">
                  <F l="E-Way Bill Number" v={form.eway_bill_number} on={(v) => setForm({ ...form, eway_bill_number: v })} tid="dp-eway" />
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">E-Way Bill File</Label>
                    <input type="file" accept=".pdf,image/*" className="hidden" id="dp-eway-file-input"
                           onChange={(e) => setEwayFile(e.target.files?.[0] || null)} data-testid="dp-eway-file-input" />
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <button type="button" onClick={() => document.getElementById("dp-eway-file-input").click()}
                              className="inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md px-2.5 h-9 text-slate-600 hover:border-slate-400 transition-colors duration-150 min-w-0"
                              data-testid="dp-eway-upload-btn">
                        <Paperclip size={12} className="flex-none" />
                        <span className="truncate max-w-[110px]">{ewayFile ? ewayFile.name : (form.eway_file_name || "Upload")}</span>
                      </button>
                      {editing && form.eway_file_id && !ewayFile && (
                        <button type="button" onClick={() => viewFile(form.eway_file_id)}
                                className="text-xs text-blue-600 hover:underline flex-none" data-testid="dp-eway-view">View</button>
                      )}
                    </div>
                  </div>
                </div>
                <F l="Invoice No." v={form.invoice_number} on={(v) => setForm({ ...form, invoice_number: v })} tid="dp-invoice" />
                <F l="Total Boxes" v={form.total_boxes} on={(v) => setForm({ ...form, total_boxes: v })} type="number" tid="dp-boxes" />
                <F l="Dispatch Date" v={form.dispatch_date} on={(v) => setForm({ ...form, dispatch_date: v })} type="date" />
                <div className="col-span-2 grid grid-cols-4 gap-3 items-end">
                  <F l="From Pincode" v={form.from_pincode} on={(v) => setForm({ ...form, from_pincode: v })} tid="dp-from-pin" />
                  <F l="To Pincode" v={form.to_pincode} on={(v) => setForm({ ...form, to_pincode: v })} tid="dp-to-pin" />
                  <F l="Distance (km)" v={form.distance_km} on={(v) => setForm({ ...form, distance_km: v })} type="number" tid="dp-distance" />
                  <Button type="button" variant="outline" onClick={calcFormDistance} className="h-10" data-testid="dp-calc-distance">
                    <Route size={13} className="mr-1.5" /> Calc Distance
                  </Button>
                </div>
                <EwayPanel form={form} orders={orders} invoices={invoices} transporters={transporters} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="dp-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="border border-slate-200 rounded-md bg-white p-4 flex flex-wrap items-end gap-3" data-testid="pin-finder">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold w-full sm:w-auto sm:pb-2.5">Pin-to-Pin Distance Finder</div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">From Pincode</Label>
          <Input value={finder.from} onChange={(e) => setFinder({ ...finder, from: e.target.value.replace(/\D/g, "").slice(0, 6) })} className="mt-1 w-32 h-9" data-testid="pf-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">To Pincode</Label>
          <Input value={finder.to} onChange={(e) => setFinder({ ...finder, to: e.target.value.replace(/\D/g, "").slice(0, 6) })} className="mt-1 w-32 h-9" data-testid="pf-to" />
        </div>
        <Button variant="outline" onClick={findDistance} disabled={finder.loading} className="h-9" data-testid="pf-find">
          <Route size={14} className="mr-1" /> {finder.loading ? "Finding..." : "Find Distance"}
        </Button>
        {finder.result && (
          <div className="text-sm text-slate-700 pb-1.5" data-testid="pf-result">
            ≈ <b>{finder.result.road_km_estimate} km</b> by road (air: {finder.result.air_km} km) · E-Way validity <b>{finder.result.eway_validity_days} day(s)</b>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="dp-tabs">
        {[["active", "Active"], ["completed", "Completed"]].map(([v, l]) => (
          <button key={v} onClick={() => setDTab(v)} data-testid={`dp-tab-${v}`}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors duration-200 ${dTab === v ? "bg-white shadow-sm font-semibold text-[#3B82F6]" : "text-slate-500 hover:text-slate-700"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Order</th>
              <th className="text-left px-4 py-2.5">Business Name</th>
              <th className="text-left px-4 py-2.5">LR / Docket</th>
              <th className="text-left px-4 py-2.5 hidden lg:table-cell">E-Way Bill</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Transporter</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Date</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Track</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((d) => (
              <tr key={d.id} className="border-t border-slate-100" data-testid={`dp-row-${d.id}`}>
                <td className="px-4 py-3 font-mono">{d.order_number}</td>
                <td className="px-4 py-3 text-slate-700" data-testid={`dp-customer-${d.id}`}>{d.customer_name || "-"}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-700 font-medium">{d.lr_number || "-"}</div>
                  <div className="text-xs text-slate-500">{d.invoice_number}{d.total_boxes > 0 ? ` · ${d.total_boxes} box(es)` : ""}</div>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <div className="font-mono text-xs text-slate-700">{d.eway_bill_number || "-"}</div>
                  {d.distance_km > 0 && <div className="text-[11px] text-slate-500">{d.from_pincode} → {d.to_pincode} · {d.distance_km} km · {d.eway_validity_days}d</div>}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <div>{d.transporter || "-"}</div>
                  {d.transporter_gstin && <div className="text-[11px] font-mono text-slate-500">{d.transporter_gstin}</div>}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">{fmtDate(d.dispatch_date)}</td>
                <td className="px-4 py-3">
                  <StatusPill status={d.delivery_status} />
                  <OutcomeBadges outcome={d.outcome} testId={`dp-outcome-${d.id}`} />
                  {d.pod_file_name && <div className="text-[11px] text-emerald-700 mt-1">POD ✓</div>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    {d.pod_file_id ? (
                      <button onClick={() => viewPod(d.pod_file_id)} className="p-1.5 text-emerald-700 hover:text-emerald-900 rounded hover:bg-emerald-50 transition-colors duration-150" title={`View POD (${d.pod_file_name || ""})`} data-testid={`dp-pod-view-${d.id}`}>
                        <FileCheck2 size={14} />
                      </button>
                    ) : (
                      <button onClick={() => pickPod(d)} className="p-1.5 text-violet-600 hover:text-violet-800 rounded hover:bg-violet-50 transition-colors duration-150" title="Upload POD — marks Delivered" data-testid={`dp-pod-upload-${d.id}`}>
                        <Upload size={14} />
                      </button>
                    )}
                    {trackLiveUrl(d) && (
                      <a href={trackLiveUrl(d)} target="_blank" rel="noreferrer"
                         className="p-1.5 text-emerald-700 hover:text-emerald-900 rounded hover:bg-emerald-50 transition-colors duration-150" title="Track Live" data-testid={`dp-tracklive-${d.id}`}>
                        <Radio size={14} />
                      </a>
                    )}
                    <a href={`${API_URL}/dispatch/${d.id}/labels`} target="_blank" rel="noreferrer"
                       className="p-1.5 text-amber-700 hover:text-amber-900 rounded hover:bg-amber-50 transition-colors duration-150"
                       title={`Print box labels (${d.total_boxes || 1} box${(d.total_boxes || 1) > 1 ? "es" : ""})`} data-testid={`dp-labels-${d.id}`}>
                      <Tag size={14} />
                    </a>
                    <a href={`${API_URL}/dispatch/${d.id}/envelope`} target="_blank" rel="noreferrer"
                       className="p-1.5 text-[#2563EB] hover:text-[#1D4ED8] rounded hover:bg-blue-50 transition-colors duration-150"
                       title="Print Envelope (#10)" data-testid={`dp-envelope-${d.id}`}>
                      <Printer size={14} />
                    </a>
                    <button onClick={() => emailDispatch(d)}
                      className={`p-1.5 rounded transition-colors duration-150 ${d.lr_number ? "text-[#3B82F6] hover:text-[#2563EB] hover:bg-blue-50" : "text-slate-300 cursor-not-allowed"}`}
                      title={d.lr_number ? "Email invoice & dispatch details to customer" : "Add LR number first"} data-testid={`dp-email-${d.id}`}>
                      <Mail size={14} />
                    </button>
                    <button onClick={() => edit(d)} className="p-1.5 text-slate-500 hover:text-[#2563EB] rounded hover:bg-slate-100 transition-colors duration-150" title="Edit" data-testid={`dp-edit-${d.id}`}>
                      <Pencil size={14} />
                    </button>
                    {isAdmin && (
                      <button onClick={() => del(d)} className="p-1.5 text-slate-500 hover:text-rose-600 rounded hover:bg-rose-50 transition-colors duration-150" title="Delete" data-testid={`dp-del-${d.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {shownDisp.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-500">{dTab === "completed" ? "No delivered dispatches yet." : "No active dispatches."}</td></tr>}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>
      <input ref={podInputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={uploadPod} data-testid="dp-pod-file-input" />

    </div>
  );
}

function F({ l, v, on, type = "text", tid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{l}</Label>
      {type === "date"
        ? <DatePicker value={v} onChange={on} className="mt-1.5" testId={tid} />
        : <Input value={v} onChange={(e) => on(e.target.value)} className="mt-1.5" type={type} data-testid={tid} />}
    </div>
  );
}

const OUR_GSTIN = "27BBQPG4486A1ZK";
const deriveHsn = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("reinforced")) return "40092100";
  if (["tube", "hose", "profile", "strip", "sleeve", "sleeving"].some((k) => n.includes(k))) return "40091100";
  if (["o'ring", "o-ring", "oring", "o ring"].some((k) => n.includes(k))) return "40169990";
  return "-";
};

function EwayPanel({ form, orders, invoices, transporters }) {
  const [nextInvNo, setNextInvNo] = useState("");
  const soId = form.sales_order_id;
  const hasInv = !!invoices.find((x) => x.sales_order_id === soId);
  useEffect(() => {
    if (soId && !hasInv) {
      api.get("/invoices/next-number").then((r) => setNextInvNo(r.data.number)).catch(() => {});
    }
  }, [soId, hasInv]);
  if (!form.sales_order_id) return null;
  const o = orders.find((x) => x.id === form.sales_order_id);
  const inv = invoices.find((x) => x.sales_order_id === form.sales_order_id);
  const cust = inv?.customer_snapshot || o?.customer_snapshot || {};
  const rawItems = inv?.items || o?.items || [];
  const items = rawItems.map((it) => ({
    ...it,
    description: it.description || it.product_name || "",
    size: it.size || it.size_label || it.size_input || "",
    unit_price: +it.unit_price || +it.sales_rate || 0,
    gst: it.gst != null ? +it.gst : 18,
  }));
  const t = transporters.find((x) => x.id === form.transporter_id);
  const taxable = items.reduce((s, it) => s + (+it.quantity || 0) * (+it.unit_price || 0), 0);
  const gstAmt = items.reduce((s, it) => s + (+it.quantity || 0) * (+it.unit_price || 0) * (+it.gst || 0) / 100, 0);
  const freight = +(inv?.freight || 0);
  const total = inv?.total ?? (taxable + gstAmt + freight * 1.18);
  const shipTo = [cust.shipping_line1, cust.shipping_line2, cust.shipping_city, cust.shipping_state].filter(Boolean).join(", ")
    || cust.shipping_address || cust.billing_address || "-";
  const interState = !(cust.gst_number || "").startsWith("27") && (cust.billing_state || "").toLowerCase() !== "maharashtra";
  const Row = ({ l, v, mono }) => (
    <div className="flex justify-between gap-3 py-1 border-b border-dashed border-slate-200 last:border-0">
      <span className="text-slate-500">{l}</span>
      <span className={`text-right text-slate-800 ${mono ? "font-mono" : "font-medium"}`}>{v || "-"}</span>
    </div>
  );
  return (
    <div className="col-span-2 border border-emerald-200 bg-emerald-50/50 rounded-lg p-4" data-testid="dp-eway-panel">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">
        E-Way Bill Portal Details {inv ? `· from Invoice ${inv.number}` : "· no invoice yet — showing order data"}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Document</div>
          <Row l="Supply Type" v="Outward — Supply" />
          <Row l="Doc Type" v="Tax Invoice" />
          <Row l="Doc No" v={inv?.number || form.invoice_number || nextInvNo} mono />
          <Row l="Doc Date" v={inv?.date ? fmtDate(inv.date) : fmtDate(new Date().toISOString())} />
          <Row l="Transaction" v={interState ? "Inter-State (IGST)" : "Intra-State (CGST+SGST)"} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Bill From / Ship To</div>
          <Row l="From GSTIN" v={OUR_GSTIN} mono />
          <Row l="Dispatch From PIN" v={form.from_pincode} mono />
          <Row l="Bill To" v={cust.company_name} />
          <Row l="To GSTIN" v={cust.gst_number || "Unregistered"} mono />
          <Row l="Ship To PIN" v={form.to_pincode} mono />
          <div className="text-[11px] text-slate-500 mt-1 leading-snug">{shipTo}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Value &amp; Transport</div>
          <Row l="Taxable Value" v={`₹${taxable.toFixed(2)}`} mono />
          <Row l="GST Amount" v={`₹${gstAmt.toFixed(2)}`} mono />
          {freight > 0 && <Row l="Freight (+18%)" v={`₹${freight.toFixed(2)}`} mono />}
          <Row l="Invoice Total" v={`₹${(+total || 0).toFixed(2)}`} mono />
          <Row l="Transporter" v={form.transporter || t?.name} />
          <Row l="Transporter GSTIN" v={form.transporter_gstin || t?.gstin} mono />
          <Row l="LR / Docket" v={form.lr_number} mono />
          <Row l="Distance" v={form.distance_km ? `${form.distance_km} km · validity ${form.eway_validity_days} day(s)` : "-"} />
        </div>
      </div>
      {items.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-slate-400 uppercase tracking-wider">
                <th className="py-1 pr-3">HSN</th><th className="py-1 pr-3">Product</th>
                <th className="py-1 pr-3 text-right">Qty</th><th className="py-1 pr-3 text-right">Taxable ₹</th>
                <th className="py-1 text-right">GST %</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t border-slate-200/70 text-slate-700">
                  <td className="py-1 pr-3 font-mono">{it.hsn || deriveHsn(it.description)}</td>
                  <td className="py-1 pr-3">{it.description}{it.size ? ` · ${it.size}` : ""}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{+it.quantity || 0} {it.unit || ""}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{((+it.quantity || 0) * (+it.unit_price || 0)).toFixed(2)}</td>
                  <td className="py-1 text-right tabular-nums">{+it.gst || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
